import { Card, CardContent } from "@/components/ui/card";
import { 
  FileText, 
  Camera, 
  DollarSign, 
  CheckCircle, 
  Clock,
  Upload,
  User,
  MessageSquare
} from "lucide-react";
import { format } from "date-fns";

interface TimelineEvent {
  id: string;
  event_type: string;
  description: string;
  created_at: string;
  event_data: any;
}

interface TimelineTabProps {
  timeline: TimelineEvent[];
}

export const TimelineTab = ({ timeline }: TimelineTabProps) => {
  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case "job_created":
        return <FileText className="w-5 h-5 text-primary" />;
      case "photo_uploaded":
        return <Camera className="w-5 h-5 text-info" />;
      case "estimate_created":
        return <DollarSign className="w-5 h-5 text-success" />;
      case "proposal_sent":
        return <FileText className="w-5 h-5 text-warning" />;
      case "status_changed":
        return <CheckCircle className="w-5 h-5 text-primary" />;
      case "document_uploaded":
        return <Upload className="w-5 h-5 text-info" />;
      case "note_added":
        return <MessageSquare className="w-5 h-5 text-muted-foreground" />;
      case "task_created":
        return <CheckCircle className="w-5 h-5 text-success" />;
      default:
        return <Clock className="w-5 h-5 text-muted-foreground" />;
    }
  };

  return (
    <div className="container mx-auto px-6 py-6">
      <h2 className="text-xl font-semibold mb-6">Job Timeline</h2>

      {timeline.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <Clock className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No timeline events yet</p>
          </div>
        </Card>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-border" />

          <div className="space-y-6">
            {timeline.map((event, index) => (
              <div key={event.id} className="relative flex gap-4">
                {/* Icon */}
                <div className="relative z-10 flex items-center justify-center w-12 h-12 rounded-full bg-card border-2 border-border">
                  {getEventIcon(event.event_type)}
                </div>

                {/* Content */}
                <Card className="flex-1">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <p className="font-medium">{event.description}</p>
                      <span className="text-xs text-muted-foreground whitespace-nowrap ml-4">
                        {format(new Date(event.created_at), "MMM d, h:mm a")}
                      </span>
                    </div>
                    {event.event_data && (
                      <div className="text-sm text-muted-foreground">
                        {Object.entries(event.event_data).map(([key, value]) => (
                          <div key={key} className="mt-1">
                            <span className="capitalize">{key.replace(/_/g, " ")}</span>:{" "}
                            <span className="font-medium">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
